// MesurePage.js
import React, { useRef, useState, useEffect } from "react";
import "./MesurePage.css";

import NomClientPopup from "./NomClientPopup";
import { saveArchive } from "./archiveManager";
import { FilesetResolver, FaceLandmarker } from "@mediapipe/tasks-vision";

function nextPaint() {
  return new Promise((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(resolve))
  );
}

// ========= UTILITAIRE : exécuter quand le navigateur est dispo =========
const runWhenIdle = (fn, timeout = 1500) => {
  if (typeof window === "undefined") return setTimeout(fn, 0);
  if ("requestIdleCallback" in window) {
    return window.requestIdleCallback(() => fn(), { timeout });
  }
  return setTimeout(fn, 0);
};

const cancelWhenIdle = (id) => {
  if (typeof window === "undefined") return;
  if ("cancelIdleCallback" in window) {
    try {
      window.cancelIdleCallback(id);
    } catch {}
  } else {
    clearTimeout(id);
  }
};

// ========= CONSTANTES =========
const EMPTY_POINTS = {
  mireG: null,
  mireD: null,
  pupOD: null,
  pupOG: null,
  L_OD: null, // coin interne bas OD
  L_OG: null, // coin interne bas OG
};

// COMPONENT
// =========================
export default function MesurePage({ goTo, data }) {
  const isReadOnly = !!data;

function getLOffset(name) {
  if (!IS_TOUCH) return { dx: 0, dy: 0 };

  if (name === "L_OD") return { dx: -L_TOUCH_OFFSET_X, dy: -L_TOUCH_OFFSET_Y };
  if (name === "L_OG") return { dx:  L_TOUCH_OFFSET_X, dy: -L_TOUCH_OFFSET_Y };

  return { dx: 0, dy: 0 };
}

  const IS_TOUCH = window.matchMedia("(any-pointer: coarse)").matches;
  const L_TOUCH_OFFSET_Y = 50;
  const L_TOUCH_OFFSET_X = 30;

  // MediaPipe
  const faceLandmarkerRef = useRef(null);
  const aiInitPromiseRef = useRef(null);
  const [aiReady, setAiReady] = useState(false);

  // refs UI
  const videoRef = useRef(null);
  const imgRef = useRef(null);
  const canvasRef = useRef(null);


  const photoBlobRef = useRef(null);
  const photoUrlRef = useRef(null);

  const wrapperRef = useRef(null);
  const overlayRef = useRef(null);

  const [zoom, setZoom] = useState({ scale: 1, x: 0, y: 0 });

const pinchRef = useRef({
  active: false,
  startDist: 0,
  startScale: 1,
  startX: 0,
  startY: 0,
  startMidX: 0,
  startMidY: 0,
});

const blockPointsUntilRef = useRef(0);

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const isPinchBlocked = () =>
  pinchRef.current.active || Date.now() < blockPointsUntilRef.current;



  // job nez (pour éviter double-clic / jobs concurrents)
  const nasalJobRef = useRef({
    running: false,
    canceled: false,
    idleId: null,
  });

  // Debug supprimé : on garde une fonction neutre pour ne pas toucher au reste
  const dbg = () => {};

  // drag des L (tangentes)
  const dragRef = useRef({
    key: null,
    pointerId: null,
  });
 const lastAimRef = useRef(null);     // mémorise la position du centre de loupe
 const pointerDownRef = useRef(false); // sait si ton doigt est posé

  // loupe throttle
  const rafRef = useRef(null);
  const lastPosRef = useRef(null);
  const lastDbgRef = useRef(0);

  // state
  const [photo, setPhoto] = useState(null);
  const [streamReady, setStreamReady] = useState(false);

  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);

  const [points, setPoints] = useState(EMPTY_POINTS);
  const [activePoint, setActivePoint] = useState("mireG");

  const [imgNaturalW, setImgNaturalW] = useState(null);
  const [imgNaturalH, setImgNaturalH] = useState(null);

  // ✅ on garde la loupe (utile), MAIS on la désactive pendant la pose/drag des L
  const [magnifier, setMagnifier] = useState(null);


  const [results, setResults] = useState(null);

  const [bridgeMM, setBridgeMM] = useState("");

  const [showPopup, setShowPopup] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [eyeBoxes, setEyeBoxes] = useState(null);
  const [pupilQuality, setPupilQuality] = useState(null);

  const setPointSafe = (key, pos) => {
  if (!key || !pos) return;
  setPoints((prev) => ({
    ...(prev || EMPTY_POINTS),  // ✅ garde tout, même si prev est null
    [key]: pos,
  }));
};
useEffect(() => {
  if (!data) return;

  // 🔒 mode lecture seule : on coupe la caméra
  stopCurrentStream();
  setStreamReady(false);

  // 🔹 recharge la photo depuis l'archive
  if (data.photoBase64) {
    setPhoto(data.photoBase64);
  }

  // 🔹 recharge les points (mire, pupilles, L)
  if (data.points) {
    setPoints(data.points);
  }

  // 🔹 recharge les résultats
  if (data.results) {
    setResults(data.results);
  }
}, [data]);
// ========= DESSIN LOUPE (centrée, coords réelles) =========
useEffect(() => {
  if (!magnifier) return;
  if (!photo) return;

  if (!imgNaturalW || !imgNaturalH) return; 
  if (!imgRef.current?.complete) return;

  const img = imgRef.current;
  const canvas = canvasRef.current;
  if (!img || !canvas) return;

  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const cw = canvas.width;
  const ch = canvas.height;

  // centre loupe en coordonnées "réelles" (natural)
  const box = getRenderedImageBox();
if (!box) return;

const { natW, natH, renderW, renderH, offsetX, offsetY } = box;

// magnifier.x/y sont en coords de l'élément <img>
const aim = lastAimRef.current || magnifier; // ✅ position exacte la plus récente
if (!aim) return;

const xInImage = aim.x - offsetX;
const yInImage = aim.y - offsetY;

const xi = Math.max(0, Math.min(xInImage, renderW - 1));
const yi = Math.max(0, Math.min(yInImage, renderH - 1));

const centerReal = {
  x: (xi * natW) / renderW,
  y: (yi * natH) / renderH,
};

  const zoom = 3;
  const srcW = cw / zoom;
  const srcH = ch / zoom;

  let sx = centerReal.x - srcW / 2;
  let sy = centerReal.y - srcH / 2;

  sx = Math.max(0, Math.min(sx, img.naturalWidth - srcW));
  sy = Math.max(0, Math.min(sy, img.naturalHeight - srcH));

  ctx.clearRect(0, 0, cw, ch);
  ctx.imageSmoothingEnabled = true;

  ctx.drawImage(img, sx, sy, srcW, srcH, 0, 0, cw, ch);

  // croix centrée dans la loupe
  ctx.save();
  ctx.beginPath();
  ctx.lineWidth = 2;
  ctx.strokeStyle = "rgba(255,255,255,0.95)";
  ctx.shadowColor = "rgba(0,0,0,0.6)";
  ctx.shadowBlur = 4;

  ctx.moveTo(cw / 2, 0);
  ctx.lineTo(cw / 2, ch);
  ctx.moveTo(0, ch / 2);
  ctx.lineTo(cw, ch / 2);

  ctx.stroke();
  ctx.restore();
}, [magnifier, photo, imgNaturalW, imgNaturalH]);

useEffect(() => {
  const wrapper = wrapperRef.current;
  const overlay = overlayRef.current;
  if (!wrapper || !overlay) return;

  const resizeCanvas = () => {
    const w = wrapper.clientWidth;
    const h = wrapper.clientHeight;
    const dpr = window.devicePixelRatio || 1;

    overlay.width = Math.max(1, Math.round(w * dpr));
    overlay.height = Math.max(1, Math.round(h * dpr));
    overlay.style.width = `${w}px`;
    overlay.style.height = `${h}px`;

    const ctx = overlay.getContext("2d");
    if (!ctx) return;

    // reset + upscale pour que 1px soit net
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    // style "blanc pur"
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 1;
    ctx.lineCap = "butt";

    const drawPlus = (x, y, size = 7) => {
      ctx.beginPath();
      ctx.moveTo(x - size, y);
      ctx.lineTo(x + size, y);
      ctx.moveTo(x, y - size);
      ctx.lineTo(x, y + size);
      ctx.stroke();
    };

    const drawT = (x, y, size = 7, down = 100) => {
      // barre horizontale
      ctx.beginPath();
      ctx.moveTo(x - size, y);
      ctx.lineTo(x + size, y);
      // barre verticale vers le bas
      ctx.moveTo(x, y);
      ctx.lineTo(x, y + down);
      ctx.stroke();
    };

    const drawL = (x, y, side /* "OD"|"OG" */, len = 160) => {
      // verticale vers le haut
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x, y - len);

      // horizontale au niveau du centre, vers gauche (OD) ou droite (OG)
      if (side === "OD") {
        ctx.moveTo(x, y);
        ctx.lineTo(x - len, y);
      } else {
        ctx.moveTo(x, y);
        ctx.lineTo(x + len, y);
      }
      ctx.stroke();
    };

    // 2) Points (mire + pupilles) + T + L
    const p = points || {};
    const drawPointIf = (key, fn) => {
      if (!p[key]) return;
      fn(p[key].x, p[key].y);
    };

    // Mires + pupilles en "+"
    drawPointIf("mireG", (x, y) => drawPlus(x, y, 7));
    drawPointIf("mireD", (x, y) => drawPlus(x, y, 7));
    drawPointIf("pupOD", (x, y) => drawT(x, y, 7, 100)); // pupille = T
    drawPointIf("pupOG", (x, y) => drawT(x, y, 7, 100)); // pupille = T

    // L
    drawPointIf("L_OD", (x, y) => {
  drawL(x, y, "OD", 160);
});

drawPointIf("L_OG", (x, y) => {
  drawL(x, y, "OG", 160);
});
  };

  // 1er dessin + redraw sur resize
  resizeCanvas();
  window.addEventListener("resize", resizeCanvas);

  // redraw quand points changent
  // (on rappelle resizeCanvas pour redessiner proprement)
  // => on le fait via requestAnimationFrame pour éviter un flash
  const id = requestAnimationFrame(resizeCanvas);

  return () => {
    cancelAnimationFrame(id);
    window.removeEventListener("resize", resizeCanvas);
  };
}, [points]);

const getTouchDistance = (t1, t2) =>
  Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);

const getTouchMidpoint = (t1, t2) => ({
  x: (t1.clientX + t2.clientX) / 2,
  y: (t1.clientY + t2.clientY) / 2,
});

const clampZoomPosition = (scale, x, y) => {
  const wrapper = wrapperRef.current;
  if (!wrapper) return { x, y };

  const minX = wrapper.clientWidth - wrapper.clientWidth * scale;
  const minY = wrapper.clientHeight - wrapper.clientHeight * scale;

  return {
    x: clamp(x, minX, 0),
    y: clamp(y, minY, 0),
  };
};

const handleZoomTouchStart = (e) => {
  if (!photo) return;
  if (e.touches.length !== 2) return;

  e.preventDefault();
  setMagnifier(null);
  dragRef.current.key = null;

  const [t1, t2] = e.touches;
  const mid = getTouchMidpoint(t1, t2);

  pinchRef.current = {
    active: true,
    startDist: getTouchDistance(t1, t2),
    startScale: zoom.scale,
    startX: zoom.x,
    startY: zoom.y,
    startMidX: mid.x,
    startMidY: mid.y,
  };
};

const handleZoomTouchMove = (e) => {
  if (!pinchRef.current.active) return;
  if (e.touches.length !== 2) return;

  e.preventDefault();

  const wrapper = wrapperRef.current;
  if (!wrapper) return;

  const rect = wrapper.getBoundingClientRect();
  const [t1, t2] = e.touches;

  const dist = getTouchDistance(t1, t2);
  const mid = getTouchMidpoint(t1, t2);

  const nextScale = clamp(
    pinchRef.current.startScale * (dist / pinchRef.current.startDist),
    1,
    4
  );

  const midLocalX = mid.x - rect.left;
  const midLocalY = mid.y - rect.top;

  const contentX =
    (midLocalX - pinchRef.current.startX) / pinchRef.current.startScale;
  const contentY =
    (midLocalY - pinchRef.current.startY) / pinchRef.current.startScale;

  let nextX = midLocalX - contentX * nextScale;
  let nextY = midLocalY - contentY * nextScale;

  const clamped = clampZoomPosition(nextScale, nextX, nextY);

  setZoom({
    scale: nextScale,
    x: clamped.x,
    y: clamped.y,
  });
};

const handleZoomTouchEnd = (e) => {
  if (!pinchRef.current.active) return;

  if (e.touches.length < 2) {
    pinchRef.current.active = false;
    blockPointsUntilRef.current = Date.now() + 220;
  }
};
// ========= HELPERS COORDS =========

const getRenderedImageBox = () => {
  const wrapper = wrapperRef.current;
  const img = imgRef.current;
  if (!wrapper || !img) return null;

  const rect = wrapper.getBoundingClientRect();
  const natW = img.naturalWidth || 1;
  const natH = img.naturalHeight || 1;

  const rectRatio = rect.width / rect.height;
  const natRatio = natW / natH;

  let renderW, renderH, offsetX, offsetY;

  if (natRatio > rectRatio) {
    renderW = rect.width;
    renderH = rect.width / natRatio;
    offsetX = 0;
    offsetY = (rect.height - renderH) / 2;
  } else {
    renderH = rect.height;
    renderW = rect.height * natRatio;
    offsetY = 0;
    offsetX = (rect.width - renderW) / 2;
  }

  return { rect, natW, natH, renderW, renderH, offsetX, offsetY };
};
const getRelativePosition = (event) => {
  const box = getRenderedImageBox();
  if (!box) return null;

  const { rect, renderW, renderH, offsetX, offsetY } = box;

  let clientX, clientY;
  if (event.touches && event.touches[0]) {
    clientX = event.touches[0].clientX;
    clientY = event.touches[0].clientY;
  } else if (event.changedTouches && event.changedTouches[0]) {
    clientX = event.changedTouches[0].clientX;
    clientY = event.changedTouches[0].clientY;
  } else {
    clientX = event.clientX;
    clientY = event.clientY;
  }

  const xInEl = clientX - rect.left;
  const yInEl = clientY - rect.top;

  const xBase = (xInEl - zoom.x) / zoom.scale;
  const yBase = (yInEl - zoom.y) / zoom.scale;

  const xInImage = xBase - offsetX;
  const yInImage = yBase - offsetY;

  if (
    xInImage < 0 ||
    yInImage < 0 ||
    xInImage > renderW ||
    yInImage > renderH
  ) {
    return null;
  }

  return { x: xBase, y: yBase };
};
// ✅ conversion coords écran -> coords réelles (natural)
const toRealCoords = (pDisplay) => {
  const box = getRenderedImageBox();
  if (!box || !pDisplay) return null;

  const { natW, natH, renderW, renderH, offsetX, offsetY } = box;

  const xInImage = pDisplay.x - offsetX;
  const yInImage = pDisplay.y - offsetY;

  const xi = Math.max(0, Math.min(xInImage, renderW));
  const yi = Math.max(0, Math.min(yInImage, renderH));

  return {
    x: (xi * natW) / renderW,
    y: (yi * natH) / renderH,
  };
};

  // Même conversion MAIS sans offset parent : cohérent avec les clics (repère image).
  const realToDisplayNoOffset = (imgEl, pReal) => {
    if (!imgEl || !pReal) return null;

    const rect = imgEl.getBoundingClientRect();
    const scaleX = rect.width / imgEl.naturalWidth;
    const scaleY = rect.height / imgEl.naturalHeight;

    return { x: pReal.x * scaleX, y: pReal.y * scaleY };
  };

// ========= DRAG L (tangentes) - version fluide & fiable =========
const startDragPoint = (key) => (e) => {
  if (isReadOnly) return;
  if (!photo) return;
  if (isPinchBlocked()) return;
  if (key !== "L_OD" && key !== "L_OG") return;

  e.preventDefault();
  e.stopPropagation();

  // On coupe la loupe uniquement pendant le drag du L
  setMagnifier(null);

  // ✅ on démarre bien le drag
  dragRef.current.key = key;

  const updateFromEvent = (ev) => {
  const pos = getRelativePosition(ev);
  if (!pos) return;

  if (key === "L_OD" || key === "L_OG") {
    const { dx, dy } = getLOffset(key);
    setPoints((prev) => ({
      ...(prev || EMPTY_POINTS),
      [key]: { x: pos.x + dx, y: pos.y + dy },
    }));
    return;
  }

  setPoints((prev) => ({
    ...(prev || EMPTY_POINTS),
    [key]: pos,
  }));
};

  // place immédiatement au premier contact
  updateFromEvent(e);

  const onMove = (ev) => {
    if (!dragRef.current.key) return;
    ev.preventDefault();
    updateFromEvent(ev);
  };

  const onUp = (ev) => {
    ev.preventDefault();
    dragRef.current.key = null;

    window.removeEventListener("pointermove", onMove, { passive: false });
    window.removeEventListener("pointerup", onUp, { passive: false });
    window.removeEventListener("pointercancel", onUp, { passive: false });
  };

  window.addEventListener("pointermove", onMove, { passive: false });
  window.addEventListener("pointerup", onUp, { passive: false });
  window.addEventListener("pointercancel", onUp, { passive: false });
};


// (Ces deux-là ne servent plus, mais on les laisse vides pour ne pas toucher ailleurs si tu les appelles encore)
const moveDragPoint = () => {};
const endDragPoint = () => {};


  // ========= IA : INIT À LA DEMANDE =========
  const ensureAIReady = async () => {
    if (faceLandmarkerRef.current) return faceLandmarkerRef.current;
    if (aiInitPromiseRef.current) return aiInitPromiseRef.current;

    aiInitPromiseRef.current = (async () => {
      const vision = await FilesetResolver.forVisionTasks(
        "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm"
      );

      const lm = await FaceLandmarker.createFromOptions(vision, {
        baseOptions: { modelAssetPath: "/models/face_landmarker.task" },
        runningMode: "IMAGE",
        numFaces: 1,
      });

      faceLandmarkerRef.current = lm;
      setAiReady(true);
      return lm;
    })();

    return aiInitPromiseRef.current;
  };

  // ========= CAMÉRA =========
  const stopCurrentStream = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach((t) => t.stop());
      videoRef.current.srcObject = null;
    }
  };

  const startStream = async (deviceId = null) => {
    if (isReadOnly) return;

    try {
      const constraints = deviceId
        ? {
            video: {
              deviceId: { exact: deviceId },
              width: { ideal: 1280 },
              height: { ideal: 720 },
              frameRate: { ideal: 30, max: 30 },
            },
            audio: false,
          }
        : {
            video: {
              facingMode: "environment",
              width: { ideal: 1280 },
              height: { ideal: 720 },
              frameRate: { ideal: 30, max: 30 },
            },
            audio: false,
          };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      const v = videoRef.current;
      if (!v) return;

      v.srcObject = stream;

      v.muted = true;
      v.playsInline = true;

      await new Promise((resolve) => {
        const done = () => resolve();
        v.addEventListener("loadedmetadata", done, { once: true });
        v.addEventListener("canplay", done, { once: true });
        setTimeout(done, 800);
      });

      try {
        const p = v.play();
        if (p && typeof p.then === "function") await p;
      } catch (e) {}

      setStreamReady(true);
    } catch (err) {
      console.error("Erreur caméra :", err);
      setStreamReady(false);
      if (!isReadOnly) {
        alert("Impossible d'accéder à la caméra. Vérifie les autorisations.");
      }
    }
  };

  const enumerateDevices = async () => {
    try {
      const list = await navigator.mediaDevices.enumerateDevices();
      const cams = list.filter((d) => d.kind === "videoinput");
      setDevices(cams);

      if (!selectedDeviceId && cams.length) {
        setSelectedDeviceId(cams[0].deviceId);
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (isReadOnly) return;
    enumerateDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isReadOnly) return;
    stopCurrentStream();
    startStream(selectedDeviceId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDeviceId]);

  useEffect(() => {
    return () => {
      stopCurrentStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ========= CAPTURE PHOTO =========
  const takePhoto = async () => {
    if (!videoRef.current) return;

    try {
      const v = videoRef.current;

      const c = document.createElement("canvas");
      c.width = v.videoWidth || 1280;
      c.height = v.videoHeight || 720;

      const ctx = c.getContext("2d");
      ctx.drawImage(v, 0, 0, c.width, c.height);

      const blob = await new Promise((resolve) =>
        c.toBlob(resolve, "image/jpeg", 0.95)
      );
      if (!blob) return;

      if (photoUrlRef.current) {
        URL.revokeObjectURL(photoUrlRef.current);
        photoUrlRef.current = null;
      }
      photoBlobRef.current = blob;

      const url = URL.createObjectURL(blob);
      photoUrlRef.current = url;

      setPoints(EMPTY_POINTS);
      setEyeBoxes(null);
      setResults(null);
      setPupilQuality(null);
      setMagnifier(null);
      setImgNaturalW(c.width);
      setImgNaturalH(c.height);
      setPhoto(url);

      stopCurrentStream();
    } catch (e) {
      console.error(e);
    }
  };

  // ========= PLACE POINT =========
const handlePlacePoint = (event) => {
  if (isReadOnly) return;
  if (!photo) return;
  if (dragRef.current.key) return;
  if (isPinchBlocked()) return;

  event?.preventDefault?.();
  event?.stopPropagation?.();

const pos = lastAimRef.current || getRelativePosition(event);
if (!pos) return;

if (activePoint === "L_OD" || activePoint === "L_OG") {
  const { dx, dy } = getLOffset(activePoint);
  setPointSafe(activePoint, { x: pos.x + dx, y: pos.y + dy });
  return;
}

setPointSafe(activePoint, pos);
};
  // ✅ Pose + drag du L directement sur l'image (plus fiable que "clic puis drag")
const handlePointerDownOnImage = (e) => {
  if (isReadOnly) return;
  if (!photo) return;
  if (isPinchBlocked()) return;

  // seulement pour les tangentes
  if (activePoint !== "L_OD" && activePoint !== "L_OG") return;

  e.preventDefault();

  const key = activePoint;
const pos = getRelativePosition(e);
if (!pos) return;

if (key === "L_OD" || key === "L_OG") {
  const { dx, dy } = getLOffset(key);
  setPointSafe(key, { x: pos.x + dx, y: pos.y + dy });
} else {
  setPointSafe(key, pos);
}

  // démarre le drag global
  dragRef.current.key = key;
  setMagnifier(null); // on ne touche pas à la loupe classique

  const onMove = (ev) => {
    if (!dragRef.current.key) return;
    ev.preventDefault();
   const p = getRelativePosition(ev);
if (!p) return;

if (key === "L_OD" || key === "L_OG") {
  const { dx, dy } = getLOffset(key);
  setPointSafe(key, { x: p.x + dx, y: p.y + dy });
} else {
  setPointSafe(key, p);
}
  };

  const onUp = (ev) => {
    ev.preventDefault();
    dragRef.current.key = null;

    window.removeEventListener("pointermove", onMove, { passive: false });
    window.removeEventListener("pointerup", onUp, { passive: false });
    window.removeEventListener("pointercancel", onUp, { passive: false });
  };

  window.addEventListener("pointermove", onMove, { passive: false });
  window.addEventListener("pointerup", onUp, { passive: false });
  window.addEventListener("pointercancel", onUp, { passive: false });
};


  // ========= LOUPE (throttle) =========
const handlePointerMove = (event) => {
  if (!photo) return;
  if (isReadOnly) return;
  if (isPinchBlocked()) return;
  if (dragRef.current.key) return;
  if (activePoint === "L_OD" || activePoint === "L_OG") return;

  const pos = getRelativePosition(event);
  if (!pos) return;

  // ✅ position exacte instantanée
  lastAimRef.current = pos;
  setMagnifier(pos);
};

  const handlePointerLeave = () => {
  if (pointerDownRef.current) return; // ✅ ne coupe pas la loupe si le doigt est posé

  lastPosRef.current = null;
  if (rafRef.current) {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }
  setMagnifier(null);
};

  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, []);
  useEffect(() => {
  return () => {
    // stop drag si on quitte la page / change de composant
    dragRef.current.key = null;
  };
}, []);
// ========= BLOQUE LE SCROLL PENDANT LA MESURE =========
useEffect(() => {
  const prevOverflow = document.body.style.overflow;

  document.body.style.overflow = "hidden"; // pas de scroll PC/tablette

  return () => {
    document.body.style.overflow = prevOverflow;
  };
}, []);

const calculate = () => {
  if (isReadOnly) return;

  const required = ["mireG", "mireD", "pupOD", "pupOG", "L_OD", "L_OG"];
  const missing = required.filter((k) => !points[k]);
  if (missing.length) {
    alert("Points manquants : " + missing.join(", "));
    return;
  }

  const mireGr = toRealCoords(points.mireG);
  const mireDr = toRealCoords(points.mireD);
  const pupODr = toRealCoords(points.pupOD);
  const pupOGr = toRealCoords(points.pupOG);
  const LODr = toRealCoords(points.L_OD);
  const LOGr = toRealCoords(points.L_OG);

  if (!mireGr || !mireDr || !pupODr || !pupOGr || !LODr || !LOGr) {
    alert("Erreur conversion coordonnées (image).");
    return;
  }

  // Échelle mm/px basée sur le gabarit (110 mm entre mires)
  const GABARIT_MM = 110;
  const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  const mireDistPx = dist(mireGr, mireDr);

  if (mireDistPx < 10) {
    alert("Distance mires trop faible : vérifie Mire G / Mire D.");
    return;
  }

  const mmPerPx = GABARIT_MM / mireDistPx;

  // ---------- DP ----------
  const bridge = parseFloat(bridgeMM) || 0;

  let DP_OD, DP_OG, DP_total;

  // vecteur des mires = axe horizontal réel
const vx = mireDr.x - mireGr.x;
const vy = mireDr.y - mireGr.y;

const norm = Math.hypot(vx, vy);

const ux = vx / norm;
const uy = vy / norm;

// projection pupille → tangente sur l'axe mires

const dpOD_px = Math.abs(
  (pupODr.x - LODr.x) * ux +
  (pupODr.y - LODr.y) * uy
);

const dpOG_px = Math.abs(
  (pupOGr.x - LOGr.x) * ux +
  (pupOGr.y - LOGr.y) * uy
);

  // conversion mm via mires
  const dpOD_raw = dpOD_px * mmPerPx;
  const dpOG_raw = dpOG_px * mmPerPx;

  // ajout 1/2 pont
  const DP_OD_base = dpOD_raw + bridge / 2;
  const DP_OG_base = dpOG_raw + bridge / 2;

  // correction perspective (photo 55–60 cm)
  const PHOTO_DISTANCE_MM = 550;
  const VERTEX_MM = 12;

  const correctionFactor =
    (PHOTO_DISTANCE_MM + VERTEX_MM) /
    PHOTO_DISTANCE_MM;

  DP_OD = DP_OD_base * correctionFactor;
  DP_OG = DP_OG_base * correctionFactor;
  DP_total = DP_OD + DP_OG;

  // ---------- Hauteurs ----------
  const H_OD = (LODr.y - pupODr.y) * mmPerPx;
  const H_OG = (LOGr.y - pupOGr.y) * mmPerPx;

  const round1 = (v) => Math.round(v * 10) / 10;

  setResults({
    DP_OD: round1(DP_OD),
    DP_OG: round1(DP_OG),
    DP_total: round1(DP_total),
    H_OD: round1(H_OD),
    H_OG: round1(H_OG),
  });
};

  // ========= RESET =========
  const reset = () => {
    if (isReadOnly) return;
    setPoints(EMPTY_POINTS);
    setResults(null);
    setPupilQuality(null);
    setEyeBoxes(null);
    setMagnifier(null);
  };

  // ========= SAVE =========
  const handleSaveClick = () => {
    if (isReadOnly) return;
    if (!results) return;
    setShowPopup(true);
  };

  const handleValidateClientName = async (clientName) => {
  try {
    setIsSaving(true);

    const blob = photoBlobRef.current;
    if (!blob) throw new Error("Aucune photo");

    // 🔹 convertir la photo en base64
    const base64 = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });

    await saveArchive({
      clientName,
      results,
      photoBase64: base64, // ✅ AJOUT IMPORTANT
      points,
    });

    setShowPopup(false);
  } catch (e) {
    console.error(e);
    alert("Erreur lors de l'enregistrement.");
  } finally {
    setIsSaving(false);
  }
};

  const handleCancelPopup = () => setShowPopup(false);

 // ========= RENDER =========
return (
  <div className="mesure-container">

    {!isReadOnly && (
      <div className="camera-row">
        <span className="camera-label">Caméra :</span>

        <select
          value={selectedDeviceId || ""}
          onChange={(e) => setSelectedDeviceId(e.target.value)}
        >
          {devices.map((d) => (
            <option key={d.deviceId} value={d.deviceId}>
              {d.label || `Caméra ${d.deviceId.slice(-4)}`}
            </option>
          ))}
        </select>
      </div>
    )}

      {/* VIDÉO (quand pas encore de photo) */}
      {!photo && !isReadOnly && (
  <>
    <div className="media-area">
      <div
        className="video-wrapper"
        style={{
          aspectRatio:
            imgNaturalW && imgNaturalH ? `${imgNaturalW} / ${imgNaturalH}` : "16 / 9",
          maxHeight: "80dvh",
          width: "100%",
        }}
      >
        <video
          ref={videoRef}
          className="video-feed"
          playsInline
          autoPlay
          muted
        />
        <canvas ref={overlayRef} className="overlay-canvas" />

        <div className="live-guides" aria-hidden="true">
          <div className="live-guide-vertical" />
          <div className="live-guide-horizontal" />
        </div>
      </div>
    </div>

    <div className="bottom-panel">
      <div className="controls">
        <button
  className="capture-button capture-left"
  onClick={takePhoto}
  disabled={!streamReady}
>
  <span className="camera-icon">📷</span>
</button>

<button
  className="capture-button capture-right"
  onClick={takePhoto}
  disabled={!streamReady}
>
  <span className="camera-icon">📷</span>
</button>

        <button onClick={() => goTo("home")}>🏠 Accueil</button>
      </div>
    </div>
  </>
)}
{photo && (
  <>
    <div className="media-area">
      <div
        className="video-wrapper"
        ref={wrapperRef}
        onTouchStart={handleZoomTouchStart}
        onTouchMove={handleZoomTouchMove}
        onTouchEnd={handleZoomTouchEnd}
        onTouchCancel={handleZoomTouchEnd}
        style={{
          aspectRatio:
            imgNaturalW && imgNaturalH ? `${imgNaturalW} / ${imgNaturalH}` : "16 / 9",
          maxHeight: "70dvh",
          width: "100%",
        }}
      >
        <div
          className="zoom-layer"
          style={{
            transform: `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.scale})`,
            transformOrigin: "0 0",
          }}
        >
          <img
            ref={imgRef}
            src={photo}
            className="video-feed"
            alt="capture"
            onLoad={(e) => {
              const im = e.currentTarget;
              setImgNaturalW(im.naturalWidth);
              setImgNaturalH(im.naturalHeight);
            }}
            onPointerDown={(e) => {
              pointerDownRef.current = true;
              handlePointerDownOnImage(e);
            }}
            onPointerMove={handlePointerMove}
            onPointerUp={(e) => {
              pointerDownRef.current = false;
              handlePlacePoint(e);
            }}
            onPointerCancel={() => {
              pointerDownRef.current = false;
              setMagnifier(null);
            }}
            onPointerLeave={handlePointerLeave}
            style={{ pointerEvents: isReadOnly ? "none" : "auto" }}
          />

          <canvas ref={overlayRef} className="overlay-canvas" />

          {/* Points */}
          {Object.entries(points || {}).map(([name, p]) => {
            if (!p) return null;

            const isL = name === "L_OD" || name === "L_OG";
            const isPupil = name === "pupOD" || name === "pupOG";

            const cls = isL
              ? `lmark ${name === "L_OD" ? "od" : "og"}`
              : `point${isPupil ? " pupil" : ""}`;

            return (
              <div
                key={name}
                className={cls}
                style={{ left: Math.round(p.x), top: Math.round(p.y) }}
                onPointerDown={isL ? startDragPoint(name) : undefined}
              />
            );
          })}

          {/* Loupe */}
          {!isReadOnly &&
            magnifier &&
            !dragRef.current.key &&
            activePoint !== "L_OD" &&
            activePoint !== "L_OG" && (
              <canvas
                ref={canvasRef}
                className="magnifier"
                width={200}
                height={200}
                style={{
                  left: magnifier.x + 140,
                  top: magnifier.y - 100,
                  transform: "translate(-50%, -50%)",
                  width: 200,
                  height: 200,
                }}
              />
            )}
        </div>
      </div>
    </div>

    <div className="bottom-panel">
      <div className="controls">
        {[
          ["mireG", "MD"],
          ["mireD", "MG"],
          ["pupOD", "OD"],
          ["pupOG", "OG"],
          ["L_OD", "L OD"],
          ["L_OG", "L OG"],
        ].map(([key, label]) => (
          <button
            key={key}
            className={activePoint === key ? "active" : ""}
            onClick={() => !isReadOnly && setActivePoint(key)}
            disabled={isReadOnly}
          >
            {label}
          </button>
        ))}

        <input
          type="number"
          step="0.5"
          min="0"
          value={bridgeMM}
          onChange={(e) => setBridgeMM(e.target.value)}
          placeholder="Pont"
          style={{
            width: 95,
            padding: "8px 10px",
            borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.22)",
            background: "rgba(0,0,0,0.45)",
            color: "#fff",
            fontSize: 14,
          }}
        />

        <button onClick={calculate} disabled={isReadOnly}>
          Calculer
        </button>

        <button onClick={reset} disabled={isReadOnly}>
          Reset
        </button>

        <button
          onClick={() => {
            if (isReadOnly) return;

            if (photoUrlRef.current) {
              URL.revokeObjectURL(photoUrlRef.current);
              photoUrlRef.current = null;
            }
            photoBlobRef.current = null;

            setPhoto(null);
            setStreamReady(false);
            startStream(selectedDeviceId);
          }}
          disabled={isReadOnly}
        >
          Nouvelle photo
        </button>

        <button onClick={() => goTo("home")}>🏠 Accueil</button>

        <button onClick={handleSaveClick} title="Enregistrer">💾</button>
      </div>

      {pupilQuality && (
        <div className="results">
          <p>{pupilQuality.msg}</p>
        </div>
      )}

      {results && (
        <div className="results glass-card">
          <h2>Résultats</h2>
          <p>DP OD : {results.DP_OD} mm</p>
          <p>DP OG : {results.DP_OG} mm</p>
          <p>DP total : {results.DP_total} mm</p>
          <p>Hauteur OD : {results.H_OD} mm</p>
          <p>Hauteur OG : {results.H_OG} mm</p>
        </div>
      )}
    </div>
  </>
)}

{showPopup && !isReadOnly && (
  <NomClientPopup
    onValidate={handleValidateClientName}
    onCancel={handleCancelPopup}
  />
)}

  </div>
);
}